<script>
  import { onMount, onDestroy } from 'svelte';
  import AppArrangement from '../components/AppArrangement.svelte';
  import AppStatus from '../components/AppStatus.svelte';
  import { getApps, reorderApps } from '../services/api';

  let apps = [];
  let loading = true;
  let saving = false;
  let saveMessage = '';
  let interval;

  onMount(async () => {
    await refresh();
    interval = setInterval(refreshStatus, 5000);
  });

  onDestroy(() => {
    if (interval) clearInterval(interval);
  });

  async function refresh() {
    try {
      const result = await getApps();
      if (result.success) {
        apps = result.apps;
      }
    } catch (err) {
      console.error('Apps refresh error:', err);
    } finally {
      loading = false;
    }
  }

  async function refreshStatus() {
    try {
      const result = await getApps();
      if (result.success) {
        // Only update status fields, preserve arrangement
        apps = apps.map(app => {
          const updated = result.apps.find(a => a.id === app.id);
          if (updated) {
            return { ...app, status: updated.status, mode: updated.mode, phase: updated.phase, error: updated.error, lastActive: updated.lastActive };
          }
          return app;
        });
      }
    } catch (err) {
      // Silently fail on status refresh
    }
  }

  async function handleReorder(e) {
    const { arrangement, apps: newApps } = e.detail;
    apps = newApps;
    saving = true;
    saveMessage = '';

    try {
      const result = await reorderApps(arrangement);
      if (result.success) {
        saveMessage = 'Order saved ✓';
        setTimeout(() => saveMessage = '', 2000);
      }
    } catch (err) {
      saveMessage = 'Failed to save';
    } finally {
      saving = false;
    }
  }
</script>

<div class="apps-page">
  <div class="page-header">
    <h1 class="section-title">
      <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <rect x="2" y="2" width="20" height="20" rx="3"/><line x1="8" y1="2" x2="8" y2="22"/>
        <line x1="16" y1="2" x2="16" y2="22"/><line x1="2" y1="12" x2="22" y2="12"/>
      </svg>
      Apps
    </h1>
    {#if saveMessage}
      <span class="save-msg" class:success={saveMessage.includes('✓')}>{saveMessage}</span>
    {/if}
  </div>

  {#if loading}
    <div class="loading-state">
      <div class="spinner"></div>
      <span>Loading apps...</span>
    </div>
  {:else}
    <div class="apps-grid">
      <AppArrangement {apps} on:reorder={handleReorder} />
      <AppStatus {apps} />
    </div>
  {/if}
</div>

<style>
  .apps-page {
    animation: fadeIn 0.3s ease;
  }

  .page-header {
    display: flex;
    align-items: center;
    gap: 16px;
    margin-bottom: 24px;
  }

  .page-header .section-title {
    margin-bottom: 0;
  }

  .save-msg {
    font-size: 12px;
    font-weight: 600;
    color: var(--neon-orange);
    animation: fadeIn 0.3s ease;
  }

  .save-msg.success {
    color: var(--neon-green);
  }

  .loading-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 14px;
    padding: 60px;
    color: var(--text-secondary);
    font-size: 14px;
  }

  .spinner {
    width: 28px;
    height: 28px;
    border: 2px solid var(--border-default);
    border-top-color: var(--neon-blue);
    border-radius: 50%;
    animation: spinSlow 0.8s linear infinite;
  }

  .apps-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 20px;
  }

  @media (max-width: 1024px) {
    .apps-grid { grid-template-columns: 1fr; }
  }
</style>
